import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Select, Tag, Tooltip, } from "antd";
import { Leave_Duration, Leave_Status, Leave_Type } from "../../../../constant/constant";
import UserLoginContext from "../../../../context/userLoginContext";
import Grid from "../../../../common/table";
import { getName } from "../../../../utility/utility";
import server from "../../../../server";
import SearchCriteria from "../../../../page-components/common/search-criteria/search-criteria";
import { SearchParams } from "../../../../page-components/common/search-criteria/type/search-criteria.type";
import { LeaveTypeSearchCriteria } from "./components/utils";
import { ILeaveRequestState, IListData } from "./leave-request.types";
import Locale from "../../../../core/locale/locale";
import dayjs from "dayjs";
import EmployeeAvatar from "../../../../page-components/employee/avatar/employee-avatar";
import LeaveAuditDetail, { IRecord } from "../common/leave-audit-detail";
import ApplyLeaveModal from "../common/leave-apply-modal";
import ConfirmActionModal from "../../../../common/ConfirmActionModal";
import { CirclePlus, CircleX, Ellipsis, Eye } from "lucide-react";
import "../../../../styles/component/leave-settings-styles.scss";
import '../../../../styles/component/leave-request.scss';
import '../../../../styles/component/common/search-criteria.scss';
import LoadingSkeleton from "../../../../page-components/common/loading-skeleton/loading-skeleton";

const LeaveRequest = () => {
    const {user} = useContext(UserLoginContext);
    const [pageNumber, setPageNumber] = useState(0);
    const isAdmin = user?.isAdmin || false;
    const pageSize = 10;
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useState<SearchParams>({});
    const [headingChange, setHeadingChange] = useState(false)
    const [cancelState, setCancelState] = useState<{ id: string | null; reason: string; }>({id: null, reason: ""});
    const [state, setState] = useState<ILeaveRequestState>({
        isModalOpen: false,
        selectedEmployee: null,
        leaveList: [],
        leavePeriod: [],
        auditRecord: {
            date: "", id: "", leaveType: "", name: "", session: "", state: ""
        },
    })

    useEffect(() => {
        if (!isAdmin) {
            setState((prev) => ({
                ...prev,
                selectedEmployee: {
                    id: user?.employeeId!,
                    name: `${user?.name.firstName} ${user?.name.lastName}`
                }
            }));
        }
        getAllLeavePeriod();
    }, [user]);

    useEffect(() => {
        if (isAdmin || state.selectedEmployee)
            fetchLeaveRequests(pageNumber);
    }, [isAdmin, state.selectedEmployee, pageNumber]);

    const getAllLeavePeriod = async () => {
        const allLeavePeriods = await server.leave.period.list();
        const ActiveLeavePeriods = allLeavePeriods.filter((leavePeriod: any) => (leavePeriod.active === true));
        setState((prev) => ({
            ...prev,
            leavePeriod: ActiveLeavePeriods
        }));
    }

    const handlePrevPage = () => {
        if (pageNumber > 0) {
            setPageNumber(pageNumber - 1);
        }
    };

    const handleNextPage = () => {
        setPageNumber(pageNumber + 1);
    }

    const filteredList = useMemo(() => {
        if (!isAdmin && state.selectedEmployee) {
            return state.leaveList.filter((val) => val.employeeId === state.selectedEmployee?.id);
        }
        return state.leaveList;
    }, [state.leaveList, state.selectedEmployee]);


    const fetchLeaveRequests = async (pageNumber: number) => {
        setLoading(true);
        try {
            let response;
            if (isAdmin) {
                response = await server.leave.request.list(searchParams, pageNumber, pageSize);
            } else if (state.selectedEmployee?.id) {
                //Employee Screen
                response = await server.leave.request.list({}, pageNumber, pageSize);
            } else {
                response = [];
            }

            const mappedLeaveList = response.map((item: any) => {
                return {
                    ...item,
                    employeeId: item.employeeId,
                    //  name: getName(item.employeeName.firstName, item.employeeName.middleName, item.employeeName.lastName),
                    employeeName: item.employeeName,
                    profilePhotoThumbnailId: item.profilePhotoThumbnailId,
                    reason: item.reason
                };
            });

            setState((prev) => ({
                ...prev,
                leaveList: mappedLeaveList
            }));

        } catch (e) {
            console.error("Error fetching leave requests:", e);
        } finally {
            setLoading(false);
        }
    };

    async function handleCancelRequest(id: string, reason: string) {
        try {
            await server.leave.request.cancel(id, reason);
            setState(prev => ({
                ...prev,
                leaveList: prev.leaveList.map(item =>
                    item.id === id ? {...item, state: "CANCELLED"} : item
                )
            }));
            setCancelState({id: null, reason: ""});

        } catch (e) {
            console.log(e);
        }
    }


    function handleLeaveAudit(record: IRecord) {
        setState((prev) => ({
            ...prev,
            auditRecord: {...record}
        }))
    }

    const getLeavePeriodLabel = (id: string) => {
        const period = state.leavePeriod.find(lp => lp.id === id);
        if (!period) return "-";
        return period.displayName;
    };

    const isCancelActionDisabled = (record: any) => {
        const leaveDate = new Date(record.date);
        const today = new Date();
        let isDisabled: boolean;
        if (record.state === 'PENDING') {
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(today.getMonth() - 1);
            isDisabled = oneMonthAgo > leaveDate;
        } else if (record.state === 'APPROVED') {
            const todayDate = new Date();
            isDisabled = todayDate > leaveDate;
        } else {
            isDisabled = record.state === 'CANCELLED' || record.state === 'REJECTED';
        }
        return isDisabled;
    }

    const columns = [
        ...(isAdmin ? [{
            title: 'Employee Name',
            dataIndex: 'name',
            align: "left",
            sortable: false,
            filterable: false,
            render: (_: any, record: IListData) => (
                <div className="employee-name-wrapper">
                    <EmployeeAvatar
                        name={record.employeeName}
                        thumbnailId={record.profilePhotoThumbnailId}
                        size={30}
                    />
                    <span>
            {getName(record.employeeName.firstName, "", record.employeeName.lastName)}
        </span>
                </div>
            )
        }] : []),
        {
            title: "Leave Period",
            dataIndex: "leavePeriodId",
            align: "left",
            render: (id: string) => getLeavePeriodLabel(id)
        },
        {
            title: 'Leave Type',
            dataIndex: 'leaveType',
            align: "left",
            sortable: false,
            render: (leaveType: any) => {
                return <span>{Leave_Type[leaveType as keyof typeof Leave_Type]}</span>
            }
        },
        {
            title: 'Session',
            dataIndex: 'session',
            align: "left",
            sortable: false,
            isDataTypeConvertable: {
                isConvertable: true,
                leaveDataType: Leave_Duration,
            },
            filterable: false,
            render: (leaveDuration: string) => {
                return Leave_Duration[leaveDuration as keyof typeof Leave_Duration]
            }
        },
        {
            title: 'Date',
            dataIndex: 'date',
            align: "left",
            sortable: false,
            render: (_: any, record: any) => record?.date ? dayjs(record.date).format(Locale.displayDate) : "-"
        },
        {
            title: 'Day',
            dataIndex: 'date',
            align: "left",
            render: (date: string) => {
                if (!date) return "-";
                const d = new Date(date);
                if (isNaN(d.getTime())) return "-";
                return d.toLocaleDateString("en-IN", {weekday: "long"});
            }
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            align: "left",
            render: (text: string) => (
                <Tooltip title={text} classNames={{root: "custom-tooltip"}}>
                    {(text && text.length > 7)
                        ? text.substring(0, 7) + "..."
                        : (text || "No reason filled")
                    }
                </Tooltip>
            )
        },
        {
            title: 'Leave Status',
            dataIndex: 'state',
            align: "left",
            render: (status: string) => (
                <>
                    {
                        <Tag className={`leave-status ${status.toLowerCase()}`}>
                            {status}
                        </Tag>
                    }
                </>
            ),
        },
        ...(!isAdmin ? [
            {
                title: 'Action',
                dataIndex: 'id',
                align: "left",
                render: (id: string, record: any) => {
                    const isDisabled = isCancelActionDisabled(record);
                    const menuItems=[
                        {
                            label:'Cancel',
                            icon:<CircleX className={`action-reject`}/>,
                            disabled:isDisabled,
                            onClick:()=>setCancelState({id, reason: ""}),
                            key:0
                        },
                        {
                            label:'View Audit',
                            icon:<Eye className="action-view"/>,
                            onClick:() => handleLeaveAudit(record),
                            key:1
                        }
                    ]

                    return (
                        <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                            <Ellipsis className={'pn-table-actions-icon'}/>
                        </Dropdown>
                    );
                }
            }
            ]
            : [{
                title: 'Action',
                dataIndex: 'id',
                align: "left",
                render: (id: string, record: any) => {
                    return (
                        <div className={'pn-table-actions'}>
                            <Tooltip title={"View Leave Audit"}>
                                <Eye className={'action-view'} onClick={() => handleLeaveAudit(record)}/>
                            </Tooltip>
                        </div>

                    )
                }
            }]),
    ];

    function handleModalOpen(val: boolean) {
        setState(prev => ({
            ...prev,
            isModalOpen: val
        }))
    }

    const handleSearch = async (params: SearchParams) => {
        setHeadingChange(true)
        try {
            const payload = {...params};

            if (params.duration && Array.isArray(params.duration)) {
                payload.duration = {
                    startDate: params.duration[0]
                        ? dayjs(params.duration[0]).format(Locale.dateFormat)
                        : null,
                    endDate: params.duration[1]
                        ? dayjs(params.duration[1]).format(Locale.dateFormat)
                        : null,
                };
            }

            const resp = await server.leave.request.list(
                payload,
                0,
                pageSize
            );
            const mappedLeaveList = resp.map((item: any) => ({
                ...item,
                name: getName(
                    item.employeeName.firstName,
                    item.employeeName.middleName,
                    item.employeeName.lastName
                ),
                reason: item.reason
            }));

            setPageNumber(0);
            setSearchParams(payload);
            setState((prev) => ({
                ...prev,
                leaveList: mappedLeaveList
            }));

        } catch (error) {
            console.error("Search failed:", error);
        }
    };

    const handleReset = () => {
        setHeadingChange(false);
    }

    return (
        <div className={"table-container leave-request"}>
            {
                isAdmin &&
                <SearchCriteria
                    searchCriteria={LeaveTypeSearchCriteria(state.leavePeriod)}
                    onSearch={handleSearch}
                    onReset={handleReset}
                    showReset={true}
                    title={"Search Leave Request"}
                    subtitle={"Find leave request  using multiple search criteria"}
                    renderTopContent={({values, onChange}) => (
                        <>
                            <div>
                                <label className={"label"}>Name</label>
                                <Input
                                    placeholder="Enter name"
                                    value={values.employeeName || ''}
                                    onChange={(e) => onChange('employeeName', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className={"label"}>Employee Code</label>
                                <Input
                                    placeholder="Enter employee code"
                                    value={values.employeeCode || ''}
                                    onChange={(e) => onChange('employeeCode', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={"label"}>Select Status</label>
                                <Select
                                    placeholder={"Select status"}
                                    onChange={(val: any) => onChange('state', val)}
                                    value={values.state}
                                    allowClear
                                    className={"label"}
                                >
                                    {(Object.keys(Leave_Status) as Array<keyof typeof Leave_Status>).map((key) =>
                                        <Select.Option value={key} key={key}>
                                            {Leave_Status[key]}
                                        </Select.Option>
                                    )}
                                </Select>
                            </div>
                        </>
                    )}
                />
            }

            <div className={'heading-and-add-leave-btn'}>
                {
                    headingChange ? (
                        <h3 className={'leave-button-title'}>Showing search results</h3>
                    ) : (
                        <h3 className={'leave-button-title'}>Leave Request</h3>
                    )
                }

                <Button
                    type="primary"
                    icon={<CirclePlus/>}
                    onClick={() => handleModalOpen(true)}
                    className={"add-data-btn"}
                >
                    Apply Leave
                </Button>
            </div>

            <div className={"leave-request__toolbar"}>

                <ApplyLeaveModal
                    isOpen={state.isModalOpen}
                    selectedEmployee={state.selectedEmployee}
                    setIsOpen={handleModalOpen}
                    isAdmin={isAdmin}
                    pageNumber={pageNumber}
                    fetchLeaveRequests={fetchLeaveRequests}
                />

            </div>
            <div className={'leave-tables'}>
                <LoadingSkeleton loading={loading}>
                    <Grid
                        bordered={true}
                        pagination={false}
                        columns={columns}
                        dataSource={filteredList as []}
                        handlePrevPage={handlePrevPage}
                        handleNextPage={handleNextPage}
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                        paginationButton={true}
                    />
                </LoadingSkeleton>
            </div>
            <ConfirmActionModal
                open={!!cancelState.id}
                title="Cancel Leave Request"
                subtitle="Are you sure you want to cancel this request?"
                showInput
                inputPlaceholder="Enter cancellation reason"
                inputValue={cancelState.reason}
                onInputChange={(val) =>
                    setCancelState((prev) => ({...prev, reason: val}))
                }
                confirmText="Yes, cancel"
                cancelText="Go back"
                confirmDisabled={false}
                onCancel={() => setCancelState({id: null, reason: ""})}
                onConfirm={() => {
                    if (cancelState.id) {
                        handleCancelRequest(cancelState.id, cancelState.reason);
                    }
                }}
            />
            {state.auditRecord && <LeaveAuditDetail record={state.auditRecord}/>}
        </div>
    )
}

export default LeaveRequest;
